import React from "react";
import { Button } from "../components/tailwind";
import { UploadSettingsData } from "./UploadSettings";

type UploadButtonProps = {
  /** Whether the button should be disabled */
  disabled: boolean;
  /** Whether the upload is currently in progress */
  submitting: boolean;
  /** The text to display when ready to upload */
  readyText: string;
  /** The text to display while uploading */
  submittingText: string;
  /** The target URL to POST the form data to */
  actionUrl: string;
  /** The upload settings data */
  settings: UploadSettingsData;
  /** Optional: The file to upload (for file uploads) */
  file?: File | null;
  /** Optional: Product IDs to upload (for PIM uploads) */
  productIds?: string[];
  /** Callback when submission starts */
  onSubmitStart: () => void;
  /** Callback when submission fails */
  onSubmitError: (error: string) => void;
};

/**
 * Shared upload button component for both file and PIM uploads
 */
export const UploadButton: React.FC<UploadButtonProps> = ({
  disabled,
  submitting,
  readyText,
  submittingText,
  actionUrl,
  settings,
  file,
  productIds,
  onSubmitStart,
  onSubmitError,
}) => {
  const handleSubmit = async (): Promise<void> => {
    if (disabled || submitting) {
      return;
    }

    onSubmitStart();

    try {
      // Create FormData for submission
      const formData = new FormData();

      // Add file if present (for file uploads)
      if (file) {
        formData.append("csv_file", file);
      }

      // Add product IDs if present (for PIM uploads)
      if (productIds && productIds.length > 0) {
        formData.append("product_ids", productIds.join("\n"));
      }

      // Add settings
      formData.append("type_of_upload", settings.type_of_upload);
      formData.append("prompt", settings.prompt);
      formData.append(
        "translate_ai_text",
        settings.translate_ai_text ? "on" : ""
      );
      formData.append("pipeline", settings.pipeline);
      formData.append("importer", settings.importer);
      formData.append("product_namespace", settings.product_namespace);
      // Product labels must be sent as JSON string (expected by MultipleSelectorField)
      formData.append(
        "product_labels",
        JSON.stringify(settings.product_labels)
      );

      // Submit to backend
      const response = await fetch(actionUrl, {
        method: "POST",
        body: formData,
      });

      if (response.redirected) {
        // Success - follow the redirect
        window.location.href = response.url;
      } else if (!response.ok) {
        // HTTP error response
        throw new Error("Upload failed");
      } else {
        // Response was OK but no redirect - check if there's an error in the response body
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          if (data.error) {
            throw new Error(data.error);
          }
        }
        // If we got here without a redirect, something unexpected happened
        throw new Error(
          "Upload completed but no redirect received. Please check your documents."
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      onSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to upload. Please try again."
      );
    }
  };

  return (
    <Button
      onClick={handleSubmit}
      disabled={disabled}
      variant="primary"
      size="lg"
    >
      {submitting ? submittingText : readyText}
    </Button>
  );
};
